import React, { useState, useEffect } from 'react';
import { payhereAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  CreditCard,
  Loader2,
  CheckCircle,
  Clock,
  Receipt,
  Banknote,
  ArrowRight,
  ShoppingCart,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { MigrateLegacyPayHereDialog } from '../components/MigrateLegacyPayHereDialog';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-LK', { style: 'currency', currency: 'LKR' }).format(amount || 0);

export default function PayHereSettlements() {
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selected, setSelected] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [netAmount, setNetAmount] = useState('');
  const [settlementDate, setSettlementDate] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [detail, setDetail] = useState(null);
  const [migrateOpen, setMigrateOpen] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [p, s, b] = await Promise.all([
        payhereAPI.getPending(),
        payhereAPI.listSettlements(),
        payhereAPI.getBankAccounts(),
      ]);
      setPending(p.data.transactions || []);
      setSettlements(s.data.settlements || []);
      setBankAccounts((b.data || []).filter((account) =>
        account.account_type === 'bank' && account.is_active !== false
      ));
    } catch (e) {
      toast.error('Failed to load PayHere data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const selectedIds = Object.keys(selected).filter((id) => selected[id]);
  const grossSelected = pending
    .filter((t) => selected[t.id])
    .reduce((sum, t) => sum + (t.gross_amount || 0), 0);

  const totalPending = pending.reduce((sum, t) => sum + (t.gross_amount || 0), 0);

  const toggleAll = (checked) => {
    const next = {};
    if (checked) pending.forEach((t) => (next[t.id] = true));
    setSelected(next);
  };

  const openSettleDialog = () => {
    if (selectedIds.length === 0) {
      toast.error('Select at least one transaction');
      return;
    }
    setNetAmount(grossSelected.toFixed(2));
    setReference('');
    setBankAccountId(bankAccounts.length === 1 ? bankAccounts[0].id : '');
    setSettlementDate(new Date().toISOString().slice(0, 10));
    setDialogOpen(true);
  };

  const feePreview = Math.max(0, grossSelected - (parseFloat(netAmount) || 0));

  const submitSettlement = async () => {
    const net = parseFloat(netAmount);
    if (!net || net <= 0) {
      toast.error('Enter a valid net amount');
      return;
    }
    if (net > grossSelected) {
      toast.error('Net amount cannot exceed gross amount');
      return;
    }
    if (!bankAccountId) {
      toast.error('Select the bank account that received the PayHere deposit');
      return;
    }
    setSubmitting(true);
    try {
      const res = await payhereAPI.createSettlement({
        transaction_ids: selectedIds,
        bank_account_id: bankAccountId,
        net_amount: net,
        settlement_date: settlementDate,
        reference: reference || null,
      });
      toast.success(res.data.message || 'Settlement recorded');
      setDialogOpen(false);
      setSelected({});
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record settlement');
    } finally {
      setSubmitting(false);
    }
  };

  const openDetail = async (id) => {
    try {
      const res = await payhereAPI.getSettlement(id);
      setDetail(res.data);
    } catch (e) {
      toast.error('Failed to load settlement');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payhere-settlements-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <CreditCard className="h-7 w-7 text-indigo-600" />
            PayHere Settlements
          </h1>
          <p className="text-slate-500 mt-1">
            Reconcile PayHere online payments held in the clearing account against actual bank deposits.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => setMigrateOpen(true)}
          data-testid="open-migrate-btn"
        >
          Migrate Legacy PayHere Sales
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card data-testid="summary-pending-count">
          <CardContent className="p-5">
            <p className="text-sm text-slate-500 flex items-center gap-2"><Clock className="h-4 w-4" /> Pending Transactions</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{pending.length}</p>
          </CardContent>
        </Card>
        <Card data-testid="summary-pending-total">
          <CardContent className="p-5">
            <p className="text-sm text-slate-500 flex items-center gap-2"><Banknote className="h-4 w-4" /> Total in Clearing</p>
            <p className="text-2xl font-bold text-amber-600 mt-1">{formatCurrency(totalPending)}</p>
          </CardContent>
        </Card>
        <Card data-testid="summary-selected-total">
          <CardContent className="p-5">
            <p className="text-sm text-slate-500 flex items-center gap-2"><CheckCircle className="h-4 w-4" /> Selected</p>
            <p className="text-2xl font-bold text-indigo-600 mt-1">{formatCurrency(grossSelected)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Pending transactions */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Pending Settlement</CardTitle>
          <Button
            onClick={openSettleDialog}
            disabled={selectedIds.length === 0}
            data-testid="record-settlement-btn"
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            Record Settlement ({selectedIds.length})
          </Button>
        </CardHeader>
        <CardContent>
          {pending.length === 0 ? (
            <div className="text-center py-12 text-slate-400" data-testid="no-pending">
              <CheckCircle className="h-10 w-10 mx-auto mb-2 text-emerald-400" />
              No PayHere transactions awaiting settlement.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-2 pr-2">
                      <Checkbox
                        checked={selectedIds.length === pending.length && pending.length > 0}
                        onCheckedChange={toggleAll}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4">Order</th>
                    <th className="py-2 pr-4">Customer</th>
                    <th className="py-2 pr-4">Entry</th>
                    <th className="py-2 pr-4 text-right">Gross Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map((t) => (
                    <tr key={t.id} className="border-b hover:bg-slate-50" data-testid={`pending-row-${t.id}`}>
                      <td className="py-2 pr-2">
                        <Checkbox
                          checked={!!selected[t.id]}
                          onCheckedChange={(c) => setSelected((prev) => ({ ...prev, [t.id]: c }))}
                          data-testid={`select-${t.id}`}
                        />
                      </td>
                      <td className="py-2 pr-4">{t.entry_date}</td>
                      <td className="py-2 pr-4 font-medium text-slate-800 flex items-center gap-1">
                        <ShoppingCart className="h-3.5 w-3.5 text-slate-400" />
                        {t.order_number || `#${t.woo_order_id || '-'}`}
                      </td>
                      <td className="py-2 pr-4">{t.customer_name || '-'}</td>
                      <td className="py-2 pr-4 text-slate-400">{t.entry_number}</td>
                      <td className="py-2 pr-4 text-right font-semibold">{formatCurrency(t.gross_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Settlement history */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Settlement History</CardTitle>
        </CardHeader>
        <CardContent>
          {settlements.length === 0 ? (
            <div className="text-center py-8 text-slate-400" data-testid="no-settlements">No settlements recorded yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4">Reference</th>
                    <th className="py-2 pr-4">Deposited To</th>
                    <th className="py-2 pr-4">Txns</th>
                    <th className="py-2 pr-4 text-right">Gross</th>
                    <th className="py-2 pr-4 text-right">Fee</th>
                    <th className="py-2 pr-4 text-right">Net Deposit</th>
                    <th className="py-2 pr-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {settlements.map((s) => (
                    <tr key={s.id} className="border-b hover:bg-slate-50" data-testid={`settlement-row-${s.id}`}>
                      <td className="py-2 pr-4">{s.settlement_date}</td>
                      <td className="py-2 pr-4">{s.reference || '-'}</td>
                      <td className="py-2 pr-4">{s.bank_account_name || 'Legacy: Main Bank'}</td>
                      <td className="py-2 pr-4">{s.transaction_count}</td>
                      <td className="py-2 pr-4 text-right">{formatCurrency(s.gross_amount)}</td>
                      <td className="py-2 pr-4 text-right text-red-600">{formatCurrency(s.fee_amount)}</td>
                      <td className="py-2 pr-4 text-right font-semibold text-emerald-600">{formatCurrency(s.net_amount)}</td>
                      <td className="py-2 pr-4 text-right">
                        <Button variant="ghost" size="sm" onClick={() => openDetail(s.id)} data-testid={`view-settlement-${s.id}`}>
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Settlement dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="settlement-dialog">
          <DialogHeader>
            <DialogTitle>Record PayHere Settlement</DialogTitle>
            <DialogDescription>
              {selectedIds.length} transaction(s) selected. Enter the net amount actually deposited into your bank.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2">
              <span className="text-sm text-slate-500">Gross Amount (Clearing)</span>
              <span className="font-semibold">{formatCurrency(grossSelected)}</span>
            </div>
            <div>
              <Label htmlFor="bank-account">Bank Account Receiving the Deposit *</Label>
              <Select value={bankAccountId} onValueChange={setBankAccountId}>
                <SelectTrigger id="bank-account" data-testid="settlement-bank-account-select">
                  <SelectValue placeholder="Select destination bank account" />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.account_name}
                      {account.bank_name ? ` - ${account.bank_name}` : ''}
                      {account.account_number ? ` (${account.account_number})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {bankAccounts.length === 0 && (
                <p className="mt-1 text-xs text-red-600">
                  No active bank accounts found. Create one under Finance → Bank &amp; Cash Accounts first.
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="net-amount">Net Amount Deposited to Bank</Label>
              <Input
                id="net-amount"
                type="number"
                step="0.01"
                value={netAmount}
                onChange={(e) => setNetAmount(e.target.value)}
                data-testid="net-amount-input"
              />
            </div>
            <div className="flex items-center justify-between rounded-md bg-red-50 px-3 py-2">
              <span className="text-sm text-red-500">Calculated PayHere Fee</span>
              <span className="font-semibold text-red-600" data-testid="fee-preview">{formatCurrency(feePreview)}</span>
            </div>
            <div>
              <Label htmlFor="settlement-date">Settlement Date</Label>
              <Input
                id="settlement-date"
                type="date"
                value={settlementDate}
                onChange={(e) => setSettlementDate(e.target.value)}
                data-testid="settlement-date-input"
              />
            </div>
            <div>
              <Label htmlFor="reference">Settlement Reference / Transaction ID (optional)</Label>
              <Input
                id="reference"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="e.g. PH-SETTLE-20260601"
                data-testid="reference-input"
              />
            </div>
            <div className="text-xs text-slate-500 rounded-md border border-slate-200 p-3 space-y-1">
              <p className="flex items-center gap-1">
                <ArrowRight className="h-3 w-3" />
                Debit: {bankAccounts.find((account) => account.id === bankAccountId)?.account_name || 'Selected Bank Account'} {formatCurrency(parseFloat(netAmount) || 0)}
              </p>
              <p className="flex items-center gap-1"><ArrowRight className="h-3 w-3" /> Debit: PayHere Fees {formatCurrency(feePreview)}</p>
              <p className="flex items-center gap-1"><ArrowRight className="h-3 w-3" /> Credit: PayHere Clearing {formatCurrency(grossSelected)}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={submitSettlement} disabled={submitting || !bankAccountId || bankAccounts.length === 0} data-testid="confirm-settlement-btn" className="bg-indigo-600 hover:bg-indigo-700">
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirm Settlement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="settlement-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Receipt className="h-5 w-5" /> Settlement Details</DialogTitle>
            <DialogDescription>Gross, fee and net breakdown with the transactions included in this settlement.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-slate-500">Date:</span> {detail.settlement_date}</div>
                <div><span className="text-slate-500">Reference:</span> {detail.reference || '-'}</div>
                <div><span className="text-slate-500">Deposited To:</span> {detail.bank_account_name || 'Legacy: Main Bank'}</div>
                <div><span className="text-slate-500">Bank:</span> {detail.bank_name || '-'}</div>
                <div><span className="text-slate-500">Gross:</span> {formatCurrency(detail.gross_amount)}</div>
                <div><span className="text-slate-500">Fee:</span> <span className="text-red-600">{formatCurrency(detail.fee_amount)}</span></div>
                <div><span className="text-slate-500">Net:</span> <span className="text-emerald-600">{formatCurrency(detail.net_amount)}</span></div>
                <div><span className="text-slate-500">JE:</span> {detail.journal_entry_number}</div>
              </div>
              <div className="border-t pt-2">
                <p className="font-medium mb-1">Included Transactions ({detail.transactions?.length || 0})</p>
                <div className="max-h-52 overflow-y-auto space-y-1">
                  {(detail.transactions || []).map((t) => (
                    <div key={t.id} className="flex justify-between text-slate-600">
                      <span>{t.entry_number} · #{t.woo_order_id || '-'}</span>
                      <span>{formatCurrency(t.gross_amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MigrateLegacyPayHereDialog open={migrateOpen} onOpenChange={setMigrateOpen} onDone={loadData} />
    </div>
  );
}
